/**
 * WILLIAM'S AUTO SORT (Fri 2 Oct 2026). His map "Care Station Build" (12:44 AM), part 1 Auto Sort, names
 * seven groups in this order: 1.1 Opt In, 1.2 Opt Out, 1.3 Stop, 1.4 Kit Request, 1.5 Result request,
 * 1.6 General Question, 1.7 Help. His answer on the office's idea 8 (his Care page, 2:04 to 2:09 AM,
 * relayed 2:24 AM): "yes - do this for each identified group. Make a folder and make the sublist there
 * and then it is also more controllable." The first three are the 01A page's lanes (lib/opt-in-lane.ts,
 * rows 400, 419 and 485), untouched. This file holds the other four, each a folder on the same page with
 * its own list of OPEN entries: Kit request, Result request, General question and Help.
 *
 * SORTING ONLY. No folder has a press, a tick box or a reply: a row opens the case and the case is worked
 * there (the row 690 rule: nothing answers a member on its own).
 *
 * EVERY RULE READS A SIGNAL THE LIST ROW ALREADY CARRIES, never a new reading of the member's words:
 *   suggestedAction  the console's own suggestion (api/src/lib/intent.ts), read from the member's own
 *                    words when the list is read: "Send kit", "Send result", "Re-fax PCP", "Reply HELP".
 *                    A text rule is a hint (confidence low); Claire's captured intent and the HELP
 *                    keyword are parsed signals (high). Only a member's own message is ever read.
 *   requestTypes     the asks recorded in the case's Requests (item 6): Claire records one at intake,
 *                    the operator records the rest in the case. Type codes only, added to the list row
 *                    for this build (api/src/care/events.ts toRow).
 *   replyClass       the texting platform's class of a text reply; OTHER is what the console calls
 *                    "Question" (REPLY_CLASS_META).
 *   replyGroupHint   the server's reading of the member's reply history (approval 188): its Help group
 *                    is the Inbound queue's own Help group (lib/reply-groups.ts rowReplyGroup).
 *   source           'help-email': a member's mail to the address the HELP answer gives (row 710).
 *
 * ONE FOLDER PER ENTRY: the FIRST of his seven, in his order, whose rule it fits. The three lanes come
 * first and keep every row they had (their rules are not touched), so a sorted folder never lists a row
 * Opt In, Opt Out or STOP lists. A reply that ANSWERS the kit offer (a yes or a no, `replyAnswer`) is
 * read by the member's last answer into Opt In or Opt Out and is in no sorted folder, even when it is not
 * the last answer (approval 188: one answer row per member and campaign).
 */
import type { EventRow, RequestType, SuggestedActionKey } from '@lib/care-model';
import { CAMPAIGN_META, REPLY_CLASS_META, REQUEST_META, SUGGESTED_META } from '@lib/care-model';
import { hayIncludes } from '@lib/hay';
import { CLOSED_STATUSES, LANE_KINDS, LANE_QUERY_KEY, OPT_IN_ROUTE, isLaneRow, issuesText, type LaneKind } from '@lib/opt-in-lane';
import { replyNoteWords, rowReplyGroup } from '@lib/reply-groups';

/** His four groups after STOP, in his order (1.4 to 1.7). The query value of each is its own address. */
export type SortKind = 'kit-request' | 'result-request' | 'general-question' | 'help';
export const SORT_KINDS: readonly SortKind[] = ['kit-request', 'result-request', 'general-question', 'help'];

/** Any of his seven folders. */
export type FolderKind = LaneKind | SortKind;

/** His seven groups in his map's order (1.1 to 1.7). The order IS the precedence (folderOf). */
export const FOLDER_ORDER: readonly FolderKind[] = [...LANE_KINDS, ...SORT_KINDS];

export function isSortKind(v: unknown): v is SortKind {
  return typeof v === 'string' && (SORT_KINDS as readonly string[]).includes(v);
}

/**
 * The sorted folder the address names (`?lane=kit-request`), or null when it names a lane or nothing.
 * The SAME query key the lanes ride (LANE_QUERY_KEY), so a reload keeps the folder; the three lanes are
 * still read by laneFromSearch, untouched, which reads every value it does not know as Opt In.
 */
export function sortFromSearch(search: string): SortKind | null {
  const v = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(LANE_QUERY_KEY);
  return isSortKind(v) ? v : null;
}

/** The search string with the folder set, the rest of the query kept; Opt In clears the key (today's address). */
export function searchWithFolder(search: string, folder: FolderKind): string {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (folder === 'optin') q.delete(LANE_QUERY_KEY);
  else q.set(LANE_QUERY_KEY, folder);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** A folder's own address, the same shape as a lane's (laneNavId): `/inbound/opt-in?lane=kit-request`. */
export function folderAddress(folder: FolderKind): string {
  return `${OPT_IN_ROUTE}${searchWithFolder('', folder)}`;
}

/* ------------------------------------------ the signals ------------------------------------------ */

/** The console's suggestions each folder takes (SUGGESTED_META's keys; lib/intent.ts on the server). */
export const SORT_SUGGESTIONS: Readonly<Record<SortKind, readonly SuggestedActionKey[]>> = {
  'kit-request': ['replacement-kit'],
  // His Resulting branch (2.4) and the action groups' Resulting heading: the letter and the PCP fax.
  'result-request': ['result-letter-resend', 'pcp-fax-resend'],
  // No suggestion rule reads a question (lib/intent.ts has none), so none is taken here.
  'general-question': [],
  // The HELP keyword in the member's own words (the poller's flag, confirmed at list time).
  help: ['help-reply'],
};

/** The recorded asks (REQUEST_META's keys) each folder takes. Help has no request type of its own. */
export const SORT_REQUESTS: Readonly<Record<SortKind, readonly RequestType[]>> = {
  'kit-request': ['replacement-kit'],
  'result-request': ['result-letter-resend', 'pcp-refax'],
  // Claire records "training-question" for a caller who only wanted help using the test (test_help).
  'general-question': ['general-inquiry', 'training-question'],
  help: [],
};

/**
 * The platform's intents that it classes OTHER and that are no question from the member: a resubscribe
 * (START) and a tracking sign-up (_tools/poll_sms_replies.py: "start / tracking_optin / other / absent
 * -> OTHER"). Every other OTHER reply is the console's "Question".
 */
export const NOT_A_QUESTION_INTENTS: readonly string[] = ['start', 'tracking_optin'];

export type SortSignal = 'suggested' | 'requested' | 'question' | 'help-text' | 'help-email';

/** One reason a row is in a folder, in the operator's words: the cell's pill and its tooltip. */
export interface SortWhy {
  signal: SortSignal;
  /** The code the signal carries (the suggestion key, the request type, the class); the slicer groups on the words. */
  code: string;
  text: string;
  title: string;
}

/** The row fields every rule here reads. */
export type SortRow = Pick<
  EventRow,
  'status' | 'replyGroupHint' | 'replyClass' | 'resubscribedAfterStop' | 'replyAnswer' | 'replyNote' | 'replyIntent'
  | 'suggestedAction' | 'suggestedActionReason' | 'suggestedActionConfidence' | 'requestTypes' | 'source' | 'channel'
>;

export const SORT_WORDING = {
  /** The strip on the page (the left menu keeps 01A, 01B and 01C; row 485). */
  stripLabel: 'Sorted folders',
  stripLead: 'Sorted:',
  /** The ONE rule for an entry that fits two folders, said on the strip and on every sorted folder. */
  orderRule: 'One folder per entry: the first of Opt In, Opt Out, STOP, Kit request, Result request, General question and Help that fits it.',
  eyebrow: 'Member Care · Inbound queue · Sorted',
  notEnabled: 'The sorted folders are not enabled on this deployment: the reply buttons (MEMBERCARE_REPLY_BUTTONS) are off, so the replies are not read and nothing is sorted here.',
  loading: 'Loading...',
  openCount: (n: number): string => `Open (${n})`,
  openHelp: 'Open entries only (new, in progress, actioned or in review). A closed entry leaves the folder.',
  sortingOnly: 'Sorting only: nothing is sent from this list and no reply goes out on its own. Open a row to work the case.',
  noun: 'entries',
  search: {
    label: 'Search this folder',
    placeholder: 'Search this folder: name, member ID, text, phone, email, why, campaign',
    clear: 'Clear',
  } as const,
  suggested: (label: string): string => `Suggested: ${label}`,
  suggestedHelp: (reason: string, confidence: string): string =>
    `The console's suggestion, read from the member's own words (${reason || 'no phrase recorded'}).${confidence === 'high' ? '' : ' A text rule: a hint, so read the message.'}`,
  requested: (label: string): string => `Requested: ${label}`,
  requestedHelp: "Recorded in the case's Requests: Claire records one at intake, the operator the rest in the case.",
  question: 'Question (text reply)',
  questionHelp: `${REPLY_CLASS_META.OTHER.help} The texting platform reads no questions as such: this is a text reply it did not read as a yes, a no, a STOP or a HELP, so read it.`,
  helpText: 'Texted HELP',
  helpTextHelp: 'The member texted HELP. The texting platform sent its help answer; answer anything further in the case.',
  helpEmail: 'HELP email',
  helpEmailHelp: 'Mail to the address the HELP answer gives (ahp@), copied to the intake mailbox (row 710).',
  columnsTip: {
    received: 'When the entry arrived, on the Pacific clock.',
    channel: 'How the member reached us.',
    why: 'What put the entry in this folder: the suggestion read from the member\'s words, an ask recorded in the case, or the texting platform\'s reading.',
    tat: 'Time since the entry arrived, ticking; frozen once the entry is sent or approved.',
    status: 'Where the entry is in the queue. A closed entry leaves this folder.',
  } as const,
} as const;

export interface SortFolderDef {
  /** The strip's words and the page title. */
  tab: string;
  title: string;
  /** His map's number for the group. */
  mapNum: string;
  lede: string;
  /** The ONE line saying what puts an entry in the folder (and, for General question, how far to trust it). */
  note: string;
  /** The grid's remembered-layout key, one per folder. */
  viewKey: string;
  empty: (client: string) => string;
  emptySearch: (q: string) => string;
}

export const SORT_FOLDERS: Readonly<Record<SortKind, SortFolderDef>> = {
  'kit-request': {
    tab: 'Kit request', title: 'Kit request', mapNum: '1.4', viewKey: 'sort-kit-request',
    lede: 'Every open entry in which the member asks for a kit, one row per entry. A texted 1 to a kit offer is Opt In, not here.',
    note: 'Here when the console suggests "Send kit" from the member\'s own words (a hint; Claire\'s kit order is firm), or the case\'s Requests record a replacement kit.',
    empty: (client: string): string => `No open kit requests for ${client}.`,
    emptySearch: (q: string): string => `No kit requests match "${q}" in this folder.`,
  },
  'result-request': {
    tab: 'Result request', title: 'Result request', mapNum: '1.5', viewKey: 'sort-result-request',
    lede: 'Every open entry in which the member asks for results: the result letter sent again, or the results faxed to the doctor. One row per entry.',
    note: 'Here when the console suggests "Send result" or "Re-fax PCP" from the member\'s own words (a hint; Claire\'s results inquiry is firm), or the case\'s Requests record a result letter resend or a re-fax to the PCP.',
    empty: (client: string): string => `No open result requests for ${client}.`,
    emptySearch: (q: string): string => `No result requests match "${q}" in this folder.`,
  },
  'general-question': {
    tab: 'General question', title: 'General question', mapNum: '1.6', viewKey: 'sort-general-question',
    lede: 'Every open entry holding a member\'s question that no other folder takes, one row per entry.',
    note: 'No signal names a question as such: here are the text replies the texting platform did not read as a yes, a no, a STOP or a HELP (the console\'s "Question"; a phone reaction, a START or a tracking sign-up never), and the cases whose Requests record a general inquiry or a training question. Most are questions, not all: read each one.',
    empty: (client: string): string => `No open general questions for ${client}.`,
    emptySearch: (q: string): string => `No general questions match "${q}" in this folder.`,
  },
  help: {
    tab: 'Help', title: 'Help', mapNum: '1.7', viewKey: 'sort-help',
    lede: 'Every open HELP from a member: the HELP texts and the mail to the HELP address. One row per entry.',
    note: 'Here when the member texted HELP (the Inbound queue\'s own Help group, or the HELP keyword in a text that came by the email copy), or wrote to the address the HELP answer gives (ahp@).',
    empty: (client: string): string => `No open HELP entries for ${client}.`,
    emptySearch: (q: string): string => `No HELP entries match "${q}" in this folder.`,
  },
};

/** A text reply that ANSWERS the kit offer: read into Opt In or Opt Out by the member's last answer, never sorted here. */
function answersTheOffer(r: Pick<EventRow, 'replyAnswer'>): boolean {
  return r.replyAnswer === 'yes' || r.replyAnswer === 'no';
}

function requested(r: Pick<EventRow, 'requestTypes'>, types: readonly RequestType[]): RequestType[] {
  const have = r.requestTypes ?? [];
  return types.filter((t) => have.includes(t));
}

/**
 * Every signal that fits a row to a folder's rule, in the operator's words; [] when none does. The
 * folder's rule alone, before the order: folderOf decides which ONE folder the row is shown in.
 */
export function sortSignals(r: SortRow, kind: SortKind): SortWhy[] {
  if (answersTheOffer(r)) return [];
  const out: SortWhy[] = [];
  const sug = r.suggestedAction ?? '';
  if (sug && SORT_SUGGESTIONS[kind].includes(sug)) {
    if (kind === 'help') {
      out.push({ signal: 'help-text', code: sug, text: SORT_WORDING.helpText, title: SORT_WORDING.helpTextHelp });
    } else {
      out.push({
        signal: 'suggested', code: sug, text: SORT_WORDING.suggested(SUGGESTED_META[sug].label),
        title: SORT_WORDING.suggestedHelp(r.suggestedActionReason ?? '', r.suggestedActionConfidence ?? ''),
      });
    }
  }
  for (const t of requested(r, SORT_REQUESTS[kind])) {
    out.push({ signal: 'requested', code: t, text: SORT_WORDING.requested(REQUEST_META[t].label), title: SORT_WORDING.requestedHelp });
  }
  if (kind === 'general-question' && r.replyClass === 'OTHER' && r.replyNote?.kind !== 'reaction'
    && !NOT_A_QUESTION_INTENTS.includes(String(r.replyIntent ?? '').trim().toLowerCase())) {
    out.unshift({ signal: 'question', code: 'OTHER', text: SORT_WORDING.question, title: SORT_WORDING.questionHelp });
  }
  if (kind === 'help') {
    // The Inbound queue's own Help group (the server's reading, else the platform's class).
    if (rowReplyGroup(r) === 'help' && !out.some((w) => w.signal === 'help-text')) {
      out.unshift({ signal: 'help-text', code: 'HELP', text: SORT_WORDING.helpText, title: SORT_WORDING.helpTextHelp });
    }
    if (r.source === 'help-email') out.push({ signal: 'help-email', code: 'help-email', text: SORT_WORDING.helpEmail, title: SORT_WORDING.helpEmailHelp });
  }
  return out;
}

/** Whether a row fits a folder's rule, before the order. */
export function fitsSort(r: SortRow, kind: SortKind): boolean {
  return sortSignals(r, kind).length > 0;
}

/**
 * THE ONE FOLDER an entry sits in: the first of his seven, in his order, whose rule it fits; null when
 * none does (it stays in the Inbound queue only). The three lanes' rules are their own (isLaneRow), read
 * first, so nothing leaves them.
 */
export function folderOf(r: SortRow): FolderKind | null {
  for (const lane of LANE_KINDS) if (isLaneRow(r, lane)) return lane;
  for (const kind of SORT_KINDS) if (fitsSort(r, kind)) return kind;
  return null;
}

/** Open = not closed: the server's terminal statuses (CLOSED_STATUSES) are the only ones a folder leaves out. */
export function isOpenEntry(r: Pick<EventRow, 'status'>): boolean {
  return !CLOSED_STATUSES.has(r.status);
}

/** A folder's rows out of the queue's, in the queue's own order: open, and this folder by the order. */
export function sortRows<T extends SortRow>(items: readonly T[], kind: SortKind): T[] {
  return items.filter((r) => isOpenEntry(r) && folderOf(r) === kind);
}

/** The strip's counts: the open rows each sorted folder lists. */
export function sortCounts(items: readonly SortRow[]): Record<SortKind, number> {
  const out: Record<SortKind, number> = { 'kit-request': 0, 'result-request': 0, 'general-question': 0, help: 0 };
  for (const r of items) {
    if (!isOpenEntry(r)) continue;
    const f = folderOf(r);
    if (f && isSortKind(f)) out[f] += 1;
  }
  return out;
}

/** The Why column's sort, search and slicer words: the signals' words, in order. */
export function whyText(r: SortRow, kind: SortKind): string {
  return sortSignals(r, kind).map((w) => w.text).join('; ');
}

/** The Why slicer's values: one per signal's words, so a row with two reasons counts under both. */
export function whyValues(r: SortRow, kind: SortKind): string[] {
  return sortSignals(r, kind).map((w) => w.text);
}

/** The reply note in plain words when the row carries one (a question from a member who answered elsewhere). */
export function sortNote(r: Pick<EventRow, 'replyNote'>): string {
  return r.replyNote ? replyNoteWords(r.replyNote) : '';
}

/**
 * THE SEARCH BOX, over this folder only and in the browser (the queue's own `q` would search the whole
 * queue): token-AND over the name, the member id (padded or not), the text, the subject, the campaign's
 * label and slug, the phone, the email, the channel, the why, the issues and the status.
 */
export function matchesSortQuery(r: EventRow, kind: SortKind, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    r.memberName, r.memberId, r.preview, r.subject, r.campaign ?? '', r.campaign ? CAMPAIGN_META[r.campaign]?.label ?? '' : '',
    r.fromPhone, r.fromAddress, r.channel, whyText(r, kind), issuesText(r), r.status.replace(/_/g, ' '),
  ].join(' ').toLowerCase();
  return hayIncludes(hay, needle);
}
